/**
 * Platform Health — Audit Assertions (A7Y-2329).
 *
 * Pass rate per audit-consistency assertion over the last 8 days, the recent
 * failures with their evidence, and — the part worth building the page for —
 * REGRESSIONS: assertions that were clean for the whole prior window and are
 * failing now. A chronically-failing assertion is a known cost; one that just
 * started failing is a new defect, and the two should never look the same.
 */
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { AssertionFinding, AssertionSummary } from "@/pages/platform-health/types";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, RefreshCw, TrendingDown } from "lucide-react";

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

function relative(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "unknown";
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function passRateColor(rate: number): string {
  if (rate >= 99) return "text-green-600 dark:text-green-400";
  if (rate >= 90) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
}

function AssertionRow({ row }: { row: AssertionSummary }) {
  return (
    <li
      data-testid="platform-health-assertion-row"
      className="flex items-center gap-3 border-b border-gray-200 dark:border-zinc-700 px-4 py-3 last:border-b-0"
    >
      {row.regression ? (
        <TrendingDown className="h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
      ) : row.failedRecent > 0 ? (
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
      ) : (
        <CheckCircle2
          className="h-4 w-4 shrink-0 text-green-600 dark:text-green-400"
          aria-hidden="true"
        />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm text-gray-900 dark:text-white">{row.assertionId}</span>
          <span className="rounded bg-gray-100 dark:bg-zinc-700 px-1.5 py-0.5 text-[11px] uppercase tracking-wide text-gray-600 dark:text-gray-300">
            {row.severity}
          </span>
          {row.regression ? (
            <span className="rounded bg-red-100 dark:bg-red-950 px-1.5 py-0.5 text-[11px] font-medium text-red-800 dark:text-red-200">
              regression
            </span>
          ) : null}
        </div>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          {row.failed} failed of {row.total} · last seen {relative(row.lastSeen)}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <div className={cn("text-sm font-semibold tabular-nums", passRateColor(row.passRate))}>
          {row.passRate}%
        </div>
        {row.failedRecent > 0 ? (
          <div className="text-xs text-red-600 dark:text-red-400">{row.failedRecent} in 24h</div>
        ) : null}
      </div>
    </li>
  );
}

function FindingRow({ finding }: { finding: AssertionFinding }) {
  return (
    <li className="border-b border-gray-200 dark:border-zinc-700 px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-gray-900 dark:text-white">
          {finding.assertion_id}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {finding.app_identifier}
          {finding.store ? ` · ${finding.store}` : ""}
          {finding.locale ? ` · ${finding.locale}` : ""}
        </span>
        <span className="text-xs text-gray-400 dark:text-zinc-500">
          {relative(finding.created_at)}
        </span>
        {finding.ticket_ref ? (
          <a
            href={`https://linear.app/a7y/issue/${finding.ticket_ref}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-blue-600 hover:underline dark:text-blue-400"
          >
            {finding.ticket_ref}
          </a>
        ) : null}
      </div>
      <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">{finding.message}</p>
      {finding.evidence != null ? (
        <details className="mt-1">
          <summary className="cursor-pointer text-xs text-gray-500 dark:text-gray-400">
            evidence
          </summary>
          <pre className="mt-1 max-h-48 overflow-auto rounded bg-gray-50 dark:bg-zinc-900 p-2 text-[11px] text-gray-700 dark:text-gray-300">
            {typeof finding.evidence === "string"
              ? finding.evidence
              : JSON.stringify(finding.evidence, null, 2)}
          </pre>
        </details>
      ) : null}
    </li>
  );
}

export function AuditAssertionsPage() {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["platform-health", "audit-assertions"],
    queryFn: () => api.getPlatformHealthAuditAssertions(),
    refetchInterval: REFRESH_INTERVAL_MS,
  });

  const payload = data?.data;
  const assertions = payload?.assertions ?? [];
  const regressions = assertions.filter((a) => a.regression);

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="Audit Assertions"
        subtitle="Audit-consistency assertion findings, pass rates, and regressions"
      />

      <div className="mb-4 flex items-center justify-between">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {payload?.generatedAt ? `Updated ${relative(payload.generatedAt)}` : "Loading…"}
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-zinc-600 px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-zinc-700"
        >
          <RefreshCw
            className={cn("h-3.5 w-3.5", isFetching && "animate-spin")}
            aria-hidden="true"
          />
          Refresh
        </button>
      </div>

      {isError ? (
        <div
          data-testid="platform-health-assertions-error"
          className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-4 text-sm text-red-800 dark:text-red-200"
        >
          Couldn&apos;t load assertion findings: {(error as Error)?.message || "unknown error"}
        </div>
      ) : null}

      {regressions.length > 0 ? (
        <div
          data-testid="platform-health-assertions-regressions"
          className="mb-4 flex items-start gap-2 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-800 dark:text-red-200"
        >
          <TrendingDown className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {regressions.length} assertion(s) were clean for the prior week and are failing now:{" "}
            {regressions.map((r) => r.assertionId).join(", ")}
          </span>
        </div>
      ) : null}

      {isLoading ? (
        <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-12 text-center text-sm text-gray-500 dark:text-gray-400">
          Loading assertion findings…
        </div>
      ) : null}

      {assertions.length > 0 ? (
        <section className="overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800">
          <header className="border-b border-gray-200 dark:border-zinc-700 px-4 py-2.5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Pass rate by assertion (8 days, prod)
            </h3>
          </header>
          <ul>
            {assertions.map((row) => (
              <AssertionRow key={row.assertionId} row={row} />
            ))}
          </ul>
        </section>
      ) : payload ? (
        <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-12 text-center text-sm text-amber-800 dark:text-amber-200">
          No assertion has recorded a finding in the last 8 days. That is a finding, not an
          all-clear — the assertion engine may have stopped writing.
        </div>
      ) : null}

      {payload && payload.recentFailures.length > 0 ? (
        <section className="mt-4 overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800">
          <header className="border-b border-gray-200 dark:border-zinc-700 px-4 py-2.5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Recent failures (48h)
            </h3>
          </header>
          <ul>
            {payload.recentFailures.map((f, idx) => (
              <FindingRow key={`${f.audit_id}-${f.assertion_id}-${idx}`} finding={f} />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
